import React, { useState, useEffect, useContext } from 'react';
import { StaffActivityCard } from './StaffActivityCard';
import { InfinitySpin } from 'react-loader-spinner';
import { SunsetAPIManagementContext } from '../../Networking/Interface/Management/SunsetAPIManagementProvider';
import { StaffInfo } from '../../Networking/Interface/Management/StaffActivityInterfaces';
import Wrapper from '../../Components/Wrapper';
import { TabPane, Tabs } from '../../Components/Tabs';
import Grid from '../../Components/Grid';
import styles from './ManagementDashboard.module.css';

const ITEMS_PER_PAGE = 12;

export const ManagementDashboard = () => {
    const api = useContext(SunsetAPIManagementContext);
    const [staffInfo, setStaffInfo] = useState<StaffInfo[] | null>(null);
    const [filteredStaffInfo, setFilteredStaffInfo] = useState<StaffInfo[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterBy, setFilterBy] = useState('lastOnline');
    const [order, setOrder] = useState('asc');
    const [includeRetired, setIncludeRetired] = useState(false);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);

    const totalPages = Math.max(1, Math.ceil(filteredStaffInfo.length / ITEMS_PER_PAGE));
    const paginatedStaff = filteredStaffInfo.slice(
        (currentPage - 1) * ITEMS_PER_PAGE,
        currentPage * ITEMS_PER_PAGE
    );

    useEffect(() => {
        const fetchStaffInfo = async () => {
            setLoading(true);
            try {
                const response = await api?.getStaffActivitySummary();
                if (response && response.success) {
                    setStaffInfo(response.data!);
                    setFilteredStaffInfo(response.data!);
                }
            } catch (err) {
                console.error('Failed to fetch staff info:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchStaffInfo();
    }, [api]);

    const applyFilters = (data: StaffInfo[]) => {
        let filtered = data.filter((staff) =>
            staff.username?.toLowerCase().includes(searchTerm.toLowerCase())
        );

        if (!includeRetired) {
            filtered = filtered.filter((staff) => staff.staffActivitySummary?.lastOnline);
        }

        switch (filterBy) {
            case 'ticketsLast7Days':
                filtered.sort((a, b) =>
                    order === 'asc'
                        ? (a.staffTicketSummary?.ticketsLast7Days || 0) - (b.staffTicketSummary?.ticketsLast7Days || 0)
                        : (b.staffTicketSummary?.ticketsLast7Days || 0) - (a.staffTicketSummary?.ticketsLast7Days || 0)
                );
                break;
            case 'totalHandledTickets':
                filtered.sort((a, b) =>
                    order === 'asc'
                        ? (a.staffTicketSummary?.totalHandledTickets || 0) - (b.staffTicketSummary?.totalHandledTickets || 0)
                        : (b.staffTicketSummary?.totalHandledTickets || 0) - (a.staffTicketSummary?.totalHandledTickets || 0)
                );
                break;
            case 'punishmentsLast7Days':
                filtered.sort((a, b) =>
                    order === 'asc'
                        ? (a.staffPunishmentSummary?.punishmentsLast7Days || 0) - (b.staffPunishmentSummary?.punishmentsLast7Days || 0)
                        : (b.staffPunishmentSummary?.punishmentsLast7Days || 0) - (a.staffPunishmentSummary?.punishmentsLast7Days || 0)
                );
                break;
            case 'lastOnline':
                filtered.sort((a, b) => {
                    const now = new Date();
                    const aYearAgo = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate()).getTime();
                    const dateA = new Date(a.staffActivitySummary?.lastOnline || aYearAgo).getTime();
                    const dateB = new Date(b.staffActivitySummary?.lastOnline || aYearAgo).getTime();
                    return order === 'asc' ? dateA - dateB : dateB - dateA;
                });
                break;
            case 'userRole':
                filtered.sort((a, b) =>
                    order === 'asc' ? a.userRole - b.userRole : b.userRole - a.userRole
                );
                break;
        }

        return filtered;
    };

    const handleSearch = () => {
        if (!staffInfo) return;
        setFilteredStaffInfo(applyFilters(staffInfo));
        setCurrentPage(1);
    };

    const handleClearFilters = () => {
        setSearchTerm('');
        setFilterBy('lastOnline');
        setOrder('asc');
        setIncludeRetired(false);
        if (staffInfo) setFilteredStaffInfo(staffInfo);
        setCurrentPage(1);
    };

    return (
        <Wrapper width="1200px">
            <Tabs defaultActiveKey="Staff">
                <TabPane tabTitle="Staff Activity" tabKey="Staff">
                    <div className={styles.filterBar}>
                        <input
                            type="text"
                            className={styles.searchInput}
                            placeholder="Search by name..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                        />
                        <select
                            className={styles.selectInput}
                            value={filterBy}
                            onChange={(e) => setFilterBy(e.target.value)}
                        >
                            <option value="lastOnline">Last Online</option>
                            <option value="ticketsLast7Days">Tickets (7 Days)</option>
                            <option value="totalHandledTickets">Total Tickets</option>
                            <option value="punishmentsLast7Days">Punishments (7 Days)</option>
                            <option value="userRole">Role</option>
                        </select>
                        <select
                            className={styles.selectInput}
                            value={order}
                            onChange={(e) => setOrder(e.target.value)}
                        >
                            <option value="asc">Low → High</option>
                            <option value="desc">High → Low</option>
                        </select>
                        <label className={styles.checkboxLabel}>
                            <input
                                type="checkbox"
                                checked={includeRetired}
                                onChange={(e) => setIncludeRetired(e.target.checked)}
                            />
                            Include Retired
                        </label>
                        <button className={styles.btnPrimary} onClick={handleSearch}>Search</button>
                        <button className={styles.btnSecondary} onClick={handleClearFilters}>Clear</button>
                    </div>

                    {loading ? (
                        <div className={styles.loader}>
                            <InfinitySpin width="200" color="#e7752e" />
                        </div>
                    ) : filteredStaffInfo.length > 0 ? (
                        <>
                            <Grid elementWidth="200px" columns={4}>
                                {paginatedStaff.map((staff, index) => (
                                    <StaffActivityCard key={index} staffInfo={staff} />
                                ))}
                            </Grid>
                            {totalPages > 1 && (
                                <div className={styles.pagination}>
                                    <button
                                        className={styles.pageBtn}
                                        disabled={currentPage === 1}
                                        onClick={() => setCurrentPage((p) => p - 1)}
                                    >
                                        ← Prev
                                    </button>
                                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                                        .filter((page) => {
                                            if (totalPages <= 7) return true;
                                            if (page === 1 || page === totalPages) return true;
                                            return Math.abs(page - currentPage) <= 1;
                                        })
                                        .reduce<(number | string)[]>((acc, page, idx, arr) => {
                                            if (idx > 0 && page - (arr[idx - 1] as number) > 1) acc.push('...');
                                            acc.push(page);
                                            return acc;
                                        }, [])
                                        .map((item, idx) =>
                                            typeof item === 'string' ? (
                                                <span key={`ellipsis-${idx}`} className={styles.pageEllipsis}>…</span>
                                            ) : (
                                                <button
                                                    key={item}
                                                    className={`${styles.pageBtn} ${currentPage === item ? styles.pageBtnActive : ''}`}
                                                    onClick={() => setCurrentPage(item)}
                                                >
                                                    {item}
                                                </button>
                                            )
                                        )}
                                    <button
                                        className={styles.pageBtn}
                                        disabled={currentPage === totalPages}
                                        onClick={() => setCurrentPage((p) => p + 1)}
                                    >
                                        Next →
                                    </button>
                                </div>
                            )}
                        </>
                    ) : (
                        <div className={styles.emptyState}>No staff members found.</div>
                    )}
                </TabPane>
            </Tabs>
        </Wrapper>
    );
};
